/**
 * ProviderConnectReturn.jsx — landing page Stripe redirects to after
 * the hosted onboarding flow (`return_url` / `refresh_url`).
 *
 * On mount: GET /api/providers/me/connect/status — which causes the
 * backend to pull fresh state from Stripe, mirror it into the
 * provider profile, and hand it back. We display the resulting
 * state (active / incomplete) and a button to return to the
 * provider dashboard.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ProviderConnectReturn() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/providers/me/connect/status");
        if (cancelled) return;
        setStatus(data);
        if (
          data.connected &&
          data.charges_enabled &&
          data.payouts_enabled &&
          data.details_submitted
        ) {
          toast.success("Stripe Connect is fully set up. Payouts are active.");
        } else if (data.connected) {
          toast.info(
            "Stripe still needs a few details. You can continue setup from your dashboard."
          );
        }
      } catch (e) {
        if (!cancelled)
          toast.error(
            e.response?.data?.detail ||
              "Could not load your Stripe Connect status."
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isActive =
    status?.connected &&
    status?.charges_enabled &&
    status?.payouts_enabled &&
    status?.details_submitted;

  return (
    <main
      data-testid="provider-connect-return"
      className="mx-auto max-w-2xl px-6 py-16"
    >
      <h1 className="font-heading text-3xl font-bold text-pp-text">
        Stripe onboarding
      </h1>
      <p className="mt-1 text-sm text-pp-text-muted">
        We've pulled your latest account status from Stripe.
      </p>

      <section className="mt-8 rounded-2xl border border-pp-border bg-pp-surface p-6 shadow-sm">
        {loading && (
          <div className="flex items-center gap-3 text-pp-text-muted">
            <Loader2
              className="h-4 w-4 animate-spin"
              aria-hidden="true"
            />
            <span className="text-sm">Checking your Stripe account…</span>
          </div>
        )}
        {!loading && status && isActive && (
          <div className="flex items-start gap-3">
            <CheckCircle2
              className="mt-0.5 h-6 w-6 text-emerald-600"
              aria-hidden="true"
            />
            <div>
              <Badge
                data-testid="return-status-active"
                className="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50"
              >
                Payouts active
              </Badge>
              <p className="mt-2 text-sm text-pp-text">
                You're all set. Payouts are enabled and will land in your
                bank account on Stripe's normal schedule.
              </p>
            </div>
          </div>
        )}
        {!loading && status && !isActive && (
          <div className="flex items-start gap-3">
            <AlertTriangle
              className="mt-0.5 h-6 w-6 text-amber-600"
              aria-hidden="true"
            />
            <div>
              <Badge
                data-testid="return-status-incomplete"
                className="border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-50"
              >
                Action needed
              </Badge>
              <p className="mt-2 text-sm text-pp-text">
                Stripe still needs a few details before we can release
                payouts. You can pick up where you left off from your
                dashboard.
              </p>
            </div>
          </div>
        )}
      </section>

      <div className="mt-6">
        <Button asChild data-testid="return-to-dashboard-button">
          <Link to="/dashboard/provider">Back to dashboard</Link>
        </Button>
      </div>
    </main>
  );
}
